/**
 * SkillsSystem - Game system for skill action processing.
 *
 * Handles tick-based processing of skilling activities.
 * Grants XP, updates affinity, and restarts actions on completion.
 */

import { type ActionDefinition, createSkillingActivityData } from '@idle-depths/shared';

import type { GameContext, GameSystem } from '@/registry/SystemRegistry';
import type { RootStore } from '@/stores/RootStore';

/**
 * Create a SkillsSystem that processes skilling actions.
 *
 * The SkillsSystem:
 * - Processes active skilling actions each tick
 * - Grants XP when actions complete
 * - Tracks action affinity (completions)
 * - Auto-restarts completed actions for idle gameplay
 *
 * @param rootStore - The root store instance
 * @returns GameSystem implementation for skills
 */
export function createSkillsSystem(rootStore: RootStore): GameSystem {
  return {
    id: 'skills',
    dependencies: [], // No dependencies - can process in parallel with location

    /**
     * Initialize skills system.
     *
     * Currently no initialization needed - action state is in CharacterData.
     *
     * @param _context - Game context (unused)
     */
    initialize(_context: GameContext): void {
      // Process any offline skilling progress
      processOfflineSkilling(rootStore);
    },

    /**
     * Process tick for skilling actions.
     *
     * Checks if current action has completed and processes rewards.
     *
     * @param _context - Game context (unused, rootStore is captured in closure)
     * @param _deltaMilliseconds - Time since last tick (unused, we use timestamps)
     */
    tick(_context: GameContext, _deltaMilliseconds: number): void {
      processSkillTick(rootStore);
    },

    /**
     * Dispose skills system.
     *
     * No cleanup needed - all state lives in stores.
     */
    dispose(): void {
      // No cleanup needed
    },
  };
}

/**
 * Process a single tick for skilling.
 *
 * Checks if the current skilling action has completed and processes it.
 *
 * @param rootStore - The root store instance
 */
function processSkillTick(rootStore: RootStore): void {
  const character = rootStore.characterStore.activeCharacter;
  if (!character) return;

  // Only process if skilling
  if (character.currentActivity.type !== 'skilling') return;

  const activity = character.currentActivity;
  const now = Date.now();

  // Check if action completed
  if (now >= activity.activityData.completesAt) {
    completeAction(rootStore);
  }
}

/**
 * Process offline skilling progress.
 *
 * Called on initialization to catch up on actions that completed while offline.
 * Processes multiple completions if enough time passed.
 *
 * @param rootStore - The root store instance
 */
function processOfflineSkilling(rootStore: RootStore): void {
  const character = rootStore.characterStore.activeCharacter;
  if (!character) return;

  // Only process if skilling
  if (character.currentActivity.type !== 'skilling') return;

  const activity = character.currentActivity;
  const now = Date.now();

  // Count how many completions occurred offline
  // Cap at a reasonable number to avoid freezing the game
  const MAX_OFFLINE_COMPLETIONS = 1000;
  let completions = 0;

  while (now >= activity.activityData.completesAt && completions < MAX_OFFLINE_COMPLETIONS) {
    completeAction(rootStore);
    completions++;

    // Re-fetch activity in case it changed (completeAction may call stopAction)
    const currentActivity = rootStore.characterStore.activeCharacter.currentActivity;
    if (currentActivity.type !== 'skilling') break;
  }

  if (completions > 0) {
    // Emit event for offline progress notification
    rootStore.gameStore.events.emit('skills:offline-progress', {
      completions,
      skillId: activity.activityData.skillId,
      actionId: activity.activityData.actionId,
    });
  }
}

/**
 * Complete a skilling action.
 *
 * Grants XP, increments affinity, and restarts the action.
 *
 * @param rootStore - The root store instance
 */
function completeAction(rootStore: RootStore): void {
  const character = rootStore.characterStore.activeCharacter;
  if (!character) return;

  const activity = character.currentActivity;
  if (activity.type !== 'skilling') return;

  const { skillId, actionId, baseTimeMs, completesAt } = activity.activityData;

  // Get action definition
  const action = rootStore.gameStore.content.get<ActionDefinition>('actions', actionId);
  if (!action) {
    // Action not found (possibly removed), stop skilling
    rootStore.skillsStore.stopAction();
    return;
  }

  // Grant XP
  rootStore.skillsStore.addXP(skillId, action.baseXP);

  // Increment affinity
  rootStore.skillsStore.incrementActionAffinity(skillId, actionId);

  // TODO: Process outputs (loot system - deferred to next phase)

  // Restart action from the completion time, not current time
  // This ensures accurate timing for offline catch-up
  const newActivityData = createSkillingActivityData(skillId, actionId, baseTimeMs);

  // Set start time based on when last action completed for accurate timing
  newActivityData.startedAt = completesAt;
  newActivityData.completesAt = completesAt + baseTimeMs;

  // Update activity directly (avoiding full startAction validation)
  character.currentActivity = {
    type: 'skilling',
    startedAt: newActivityData.startedAt,
    progress: 0,
    activityData: newActivityData,
  };
  character.updatedAt = Date.now();
  character.needsSync = true;

  // Save to localStorage
  rootStore.characterStore['saveCharacterToLocalStorage'](character);
}
